<script setup lang="ts">
import type { AuthChallengeSession } from '~/types/interfaces'
import { useActing } from '~/composables/api/useActing'

useState<AuthChallengeSession | undefined>(
  'authChallengeSession',
  () => undefined,
)
useState<boolean>('bgmEnabled', () => true)

// OGP global defaults (og:url, og:type, og:locale)
const runtimeConfig = useRuntimeConfig()
const route = useRoute()
useSeoMeta({
  ogUrl: computed(() => `${runtimeConfig.public.serviceDomain}${route.fullPath}`),
  ogType: 'website',
  ogLocale: 'ja_JP',
})

const router = useRouter()
const { loading, startLoading } = useGlobalLoading()

onMounted(async () => {
  router.beforeEach((to, _from, next) => {
    if (to.meta?.usePageReady) startLoading()
    next()
  })

  await document.fonts.ready
  document.body.classList.add('font-loaded')
})

const authInfoStore = useAuthInfoStore()
const authStateStore = useAuthStateStore()
const isAuthenticated = computed(() => authInfoStore.isAuthenticated)
const isSessionExpired = computed(() => authStateStore.isSessionExpired)
const { start, stop } = useNotificationPoller()
const { refreshStatus: refreshActingStatus } = useActing()
const actingStore = useActingStore()

// Hydrate acting status whenever the user becomes authenticated (#628).
//
// We deliberately do NOT gate by `gameStateStore.user.isConsultSupport`: that flag
// is unreliable while acting because /api/v1/state is acting-wrapped and returns
// the subject's view (where isConsultSupport=false). Earlier we cleared the store
// on that flag flipping, which made the banner disappear on every navigation.
//
// Instead: GET /api/v1/admin/acting is in `_ACTING_BYPASS_ROUTES` so it always
// returns the *real* user's session. Non-admins simply get active=false (no row
// exists since they can never start one), so calling on every authenticated mount
// is safe and avoids the wrap/unwrap confusion.
watch(
  isAuthenticated,
  async (authed) => {
    if (authed) {
      try {
        await refreshActingStatus()
      }
      catch (e) {
        console.error('[app] acting status hydrate failed', e)
      }
    }
    else {
      actingStore.clear()
    }
  },
  { immediate: true },
)

// poller の起動/停止は「認証 OK」かつ「セッション切れダイアログが出ていない」とき (#600)。
// `isAuthenticated` の computed は Date.now() 非リアクティブなので時間経過で再評価
// されないが、セッション切れ判定 (`markSessionExpired`) はフェッチの 401 リトライ失敗で
// 確実に立つので、これを stop シグナルとして使う。
watch(
  [isAuthenticated, isSessionExpired],
  ([authed, expired]) => {
    if (authed && !expired) {
      console.log('Starting polling notifications...')
      start()
      useSound().preloadAll()
    }
    else {
      console.log('Stopping polling notifications...')
      stop()
    }
  },
  { immediate: true },
)
</script>

<template>
  <v-app id="app">
    <ActingBanner />
    <NuxtLayout>
      <!--
        #796: ここに明示 key を置いてはいけない。app.vue の `$route` は eager な
        vue-router の route なので、レイアウトが変わる遷移では NuxtLayout 内部の
        LayoutProvider（key = レイアウト名）と二重に key が動き、ページが
        「mount → 数 ms で unmount → 再 mount」する。onMounted の副作用が 2 回走り、
        /works/<slug> では open_game / play が二重計上されていた。
        NuxtPage の既定 key は params を埋めた matched path なので、
        /works/A → /works/B のような params 差分だけの遷移でも再生成される。
      -->
      <NuxtPage />
    </NuxtLayout>

    <!-- #751: いいね/お気に入りのサインイン導線（全露出面で共有する単一ダイアログ）。 -->
    <DialogSignInActionDialog />

    <!-- #795: 匿名プレイの離脱時サインアップ導線。WorkPlay はページ遷移で unmount する
         ので、ダイアログ本体はここに置く。 -->
    <DialogPlaySignupCtaDialog />

    <!-- ローディング画面 -->
    <v-overlay
      :model-value="loading"
      persistent
      scrim="black"
      opacity="0.88"
      class="loading-overlay"
      :contained="false"
    >
      <div class="center">
        <img
          src="/images/code_and_magic_black_textlogo.svg"
          alt="Code & Magic"
          class="logo"
        >
        <div class="text">
          Now Loading...
        </div>
        <v-progress-linear
          class="bar"
          indeterminate
        />
      </div>
    </v-overlay>
  </v-app>
</template>

<style>
body {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
}

body.font-loaded {
  font-family: 'Noto Sans JP', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
}
</style>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Vuetifyのv-overlayはデフォでposition: fixedだが、明示した方が安定 */
.loading-overlay {
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  text-align: center;
}

.logo {
  width: 240px;
  animation: pulse 1.8s ease-in-out infinite alternate;
  filter: invert(1);
}

.text {
  margin-top: 24px;
  animation: blink 1.4s ease-in-out infinite;
}

.bar {
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 99px;
}

/* Animations */
@keyframes pulse {
  from {
    opacity: 0.6;
    transform: scale(0.97);
  }

  to {
    opacity: 1;
    transform: scale(1.04);
  }
}

@keyframes blink {

  0%,
  100% {
    opacity: 0.3;
  }

  50% {
    opacity: 1;
  }
}
</style>
